import { DefaultLoader, LoaderContainer } from 'components/Loader';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  setOrder,
  setPage,
  setSearch,
  setTags,
  setTake,
} from 'store/actions/admin/binomeLists';
import { StoreState } from 'store/reducers';
import useSWR from 'swr';
import Binome from 'types/Binome';
import GetOrderValue from 'types/GetOrderValue';
import Tags from 'types/Tags';
import { Action } from 'types/typeguards/reducerActions';
import { getIsMenteeStatus } from 'utils/menteeTags';
import getTagsQueryParams from 'utils/tagQueryParam';
import Head from './index-components/Head';
import BinomesFilters from './index-components/BinomesFilters';
import BinomesList from './index-components/BinomesList';

export const tagColumnNames = ['status', 'referee'] as const;
export type TagColumnNames = typeof tagColumnNames[number];

type RetrievedData = {
  binomes: Binome[] | undefined;
  count: number | undefined;
  pages: number | undefined;
  filteredCount: number | undefined;
};

export const sortableColumns = ['createdAt', 'status'] as const;
export type OrderValue = GetOrderValue<typeof sortableColumns[number]>;

type Props = {
  page: number;
  setPage: (page: number) => void;
  search: string;
  setSearch: (search: string) => void;
  tags: Tags<TagColumnNames>;
  setTags: (tag: Tags<TagColumnNames>) => void;
  take: number;
  setTake: (take: number) => void;
  order: OrderValue;
  setOrder: (order: OrderValue) => void;
};

type ContextType = RetrievedData & { loading: boolean } & Props;
export const BinomesContext = React.createContext({} as ContextType);
const { Provider } = BinomesContext;

const Binomes: FC<Props> = ({
  children,
  page,
  setPage,
  search,
  setSearch,
  tags,
  setTags,
  take,
  setTake,
  order,
  setOrder,
}) => {
  const { data, error } = useSWR<RetrievedData>(
    `/mentorship/binomes/?page=${page}&take=${take}${
      search ? `&search=${search}` : ''
    }${
      tags.status?.length
        ? tags.status
            .map((tag) =>
              getIsMenteeStatus(tag)
                ? `&menteeTags=${tag}`
                : `&mentorshipTags=${tag}`,
            )
            .join('')
        : ''
    }${getTagsQueryParams<TagColumnNames>(tags, ['referee'])}&order=${order}`,
  );

  const [retrievedData, setRetrievedData] = useState<RetrievedData>({
    binomes: undefined,
    count: undefined,
    pages: undefined,
    filteredCount: undefined,
  });
  useEffect(() => {
    if (data) setRetrievedData(data);
  }, [data]);

  useEffect(() => {
    if (data?.pages && data?.pages <= page) setPage(0);
  }, [data, page, setPage]);

  const value = useMemo(
    () => ({
      ...retrievedData,
      page,
      setPage,
      search,
      setSearch,
      tags,
      setTags,
      loading: !data,
      take,
      setTake,
      order,
      setOrder,
    }),
    [
      page,
      setPage,
      search,
      setSearch,
      tags,
      setTags,
      retrievedData,
      data,
      take,
      setTake,
      order,
      setOrder,
    ],
  );

  return retrievedData.binomes || error ? (
    <Provider value={value}>{children}</Provider>
  ) : (
    <LoaderContainer>
      <DefaultLoader size={1.5} />
    </LoaderContainer>
  );
};

const Usage: FC<Props> = (props) => (
  <Binomes {...props}>
    <Head />
    <BinomesFilters />
    <BinomesList />
  </Binomes>
);

const mapStateToProps = (state: StoreState) => ({
  page: state.admin.binomeLists.main.page,
  search: state.admin.binomeLists.main.search,
  tags: state.admin.binomeLists.main.tags,
  take: state.admin.binomeLists.main.take,
  order: state.admin.binomeLists.main.order,
});

const mapDispatchToProps = (dispatch: (action: Action) => void) => ({
  setPage: (page: number) => dispatch(setPage('main', page)),
  setSearch: (search: string) => dispatch(setSearch('main', search)),
  setTags: (tags: Tags<TagColumnNames>) =>
    dispatch(setTags<TagColumnNames>('main', tags)),
  setTake: (take: number) => dispatch(setTake('main', take)),
  setOrder: (order: OrderValue) => dispatch(setOrder('main', order)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Usage);